import {
  type VarianceAnalysisScope,
  type VarianceAnalysisSearch,
  type VarianceQuickFilter,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import {
  QUICK_FILTER_SCOPE_KEYS,
  VARIANCE_QUICK_FILTER_SLICES,
} from '../-components/QuickFilterBar'

/**
 * The preset the quick-filter bar shows before anything has been clicked.
 *
 * Shared rather than page-local because the drill chain has to resolve the product category the same
 * way the heat map does — a drill that resolved it differently would report a different category's
 * figures than the cell the reader clicked.
 */
export const DEFAULT_VARIANCE_QUICK_FILTER: VarianceQuickFilter = 'cigarettes'

/**
 * The scope the page queries with, resolved from the URL and the selected quick-filter preset together.
 *
 * The preset has to be resolved rather than only read back out of the URL. Before the bar has been clicked
 * the URL carries none of the preset's params, yet the bar is already showing one as selected — so reading
 * only the URL reported Cigarettes while querying the whole of FMC. Its material group, pack sizes and
 * brand houses never reached the query, and the breakdown came back an order of magnitude larger than the
 * preset describes.
 *
 * The URL wins wherever it carries a key, which is what keeps a hand-built link scoping. The two can only
 * disagree before the first click: selecting a preset writes every key it owns.
 */
export function resolveVarianceScope(
  search: VarianceAnalysisSearch,
  defaultQuickFilter: VarianceQuickFilter,
): VarianceAnalysisScope {
  const slice = VARIANCE_QUICK_FILTER_SLICES[search.quickFilter ?? defaultQuickFilter]

  const presetScope = Object.fromEntries(
    QUICK_FILTER_SCOPE_KEYS.map((key) => [key, search[key] ?? slice[key]]),
  ) as Partial<Record<(typeof QUICK_FILTER_SCOPE_KEYS)[number], string[]>>

  return {
    productCategory: search.productCategory ?? slice.productCategory,
    ...presetScope,
    // Not preset-owned: read straight from the URL.
    productType: search.productType,
    brand: search.brand,
    variant: search.variant,
    province: search.province,
  }
}
